import { differenceInSeconds } from 'date-fns'
import { debounce } from 'lodash-es'
import { ContextType, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useFlag } from '@/feature/feature-flags'
import { calculateZoomForBounds, MapContext, reverseGeocode, useMapZoom } from '@/feature/map'
import { ActiveAssetContext, useSingleGeoFence } from '@/feature/observatory'
import { useGeoFenceEvents } from '@/feature/observatory/api/geoFenceEvents'
import { captureError } from '@/utils/captureError'

import { Asset } from '@/lib/features/Assets/interfaces'

import { AssetLocationHistory, useAssetLocationHistory } from '../../api/useAssetLocationHistory'
import { ReplayContext } from '../../contexts/ReplayContext'
import { Position, ReplayClusterMethod, RoutePoint } from './Replay.types'
import {
  buildGeofenceRoutePoints,
  buildMovementRoutePoints,
  createDistanceCalculator,
} from './Replay.utils'

/** Hides map overlays (tooltips, asset cards) when the user clicks away or drags the map. */
export const useHideOnMapInteraction = (hide: () => void) => {
  const map = useContext(MapContext)

  useEffect(() => {
    const listener = map?.map?.addListener('dragstart', hide)
    return listener?.remove?.bind(listener)
  }, [map?.map, hide])

  useEffect(() => {
    document.addEventListener('click', hide)
    return () => document.removeEventListener('click', hide)
  }, [hide])
}

export const useReplayRoute = (
  asset: Asset,
  startDate: Date,
  endDate: Date,
  stepSize: string,
  history?: AssetLocationHistory,
) => {
  const map = useContext(MapContext)

  const periodInSec = useMemo(() => differenceInSeconds(new Date(), startDate), [startDate])

  const { data: fetchedHistory } = useAssetLocationHistory(
    history ? null : asset.vehicleBridgeId,
    `${periodInSec}s`,
    stepSize,
  )
  const assetHistory = history ?? fetchedHistory

  const { data: eventsData } = useGeoFenceEvents()

  const geofenceHistory = useMemo(
    () => buildGeofenceRoutePoints(eventsData, asset.vehicleBridgeId, startDate, endDate),
    [eventsData, asset.vehicleBridgeId, startDate, endDate],
  )

  const assetMovementHistory = useMemo(() => {
    const projection = map?.map?.getProjection()
    const project = (position: Position) =>
      projection?.fromLatLngToPoint({ lat: position.latitude, lng: position.longitude })
    return buildMovementRoutePoints(assetHistory, project, startDate, endDate)
  }, [assetHistory, map?.map, startDate, endDate])

  return useMemo<RoutePoint[]>(
    () =>
      (assetMovementHistory as RoutePoint[])
        .concat(geofenceHistory)
        .sort((a, b) => a.when.getTime() - b.when.getTime()),
    [assetMovementHistory, geofenceHistory],
  )
}

/** Pans/zooms the map to fit the route until the user zooms or drags manually. */
export const useReplayMapBounds = (route: RoutePoint[], startDate: Date, endDate: Date) => {
  const map = useContext(MapContext)
  const [userInteracted, setUserInteracted] = useState(false)

  useEffect(() => {
    if (!map?.map) return
    const zoomListener = map.map.addListener('zoom_changed', () => setUserInteracted(true))
    const dragListener = map.map.addListener('dragend', () => setUserInteracted(true))
    return () => {
      zoomListener?.remove?.()
      dragListener?.remove?.()
    }
  }, [map?.map])

  useEffect(() => {
    if (!map?.map || userInteracted || route.length === 0) return

    if (route.length === 1) {
      map.panTo({ position: route[0], zoom: 15 })
      return
    }

    const bounds = new window.google.maps.LatLngBounds()
    for (const point of route) {
      bounds.extend(point)
    }
    const targetZoom = calculateZoomForBounds(bounds, map.map.getDiv().getBoundingClientRect())
    map.panTo({ position: bounds.getCenter(), zoom: targetZoom })
    // Refit only when the route size or window changes, not on every route identity change.
  }, [route.length, startDate, endDate, map, userInteracted])
}

export const useReplay = (
  asset: Asset,
  startDate: Date,
  endDate: Date,
  stepSize: string,
  history?: AssetLocationHistory,
) => {
  const route = useReplayRoute(asset, startDate, endDate, stepSize, history)
  useReplayMapBounds(route, startDate, endDate)

  const [shownAsset, setShownAsset] = useState<Asset>()
  const hideAsset = useCallback(() => setShownAsset(undefined), [])
  useHideOnMapInteraction(hideAsset)

  const activeAssetState = useMemo(
    () =>
      ({
        activeAsset: shownAsset ? asset : undefined,
        setActiveAsset: setShownAsset,
        clearActiveAsset: () => setShownAsset(undefined),
      }) satisfies ContextType<typeof ActiveAssetContext>,
    [shownAsset, asset],
  )

  const replayValues = useMemo(
    () =>
      ({
        available: false,
        replayAsset: asset,
        setReplayAsset: () => {},
        hoveredTimestamp: null,
        setHoveredTimestamp: () => {},
      }) satisfies ContextType<typeof ReplayContext>,
    [asset],
  )

  return { route, setShownAsset, activeAssetState, replayValues }
}

export const useReplayMarkers = (route: RoutePoint[]) => {
  const map = useContext(MapContext)
  const rawZoom = useMapZoom(map?.map)
  // Fade distances are zoom-dependent, but recomputing every marker's position on
  // every raw zoom_changed tick (many/sec during a scroll gesture) is expensive at
  // marker counts in the thousands — settle on the zoom level once ticks pause.
  const [zoom, setZoom] = useState(rawZoom)
  const debouncedSetZoom = useMemo(() => debounce(setZoom, 150), [])
  useEffect(() => {
    debouncedSetZoom(rawZoom)
  }, [rawZoom, debouncedSetZoom])
  useEffect(() => debouncedSetZoom.cancel, [debouncedSetZoom])

  const clusterDistance = useFlag('replayConfig.cluster.distance', 36) / 2 ** (zoom ?? 1)
  const clusterMethod = useFlag<ReplayClusterMethod>('replayConfig.cluster.method', 'RADIUS')
  const minFadeDistance = useFlag('replayConfig.cluster.fadeOffsetDistance', 16) / 2 ** (zoom ?? 1)
  const fadeDistance = useFlag('replayConfig.cluster.fadeDistance', 18) / 2 ** (zoom ?? 1)
  const cluster = useFlag('replayConfig.cluster.enabled', false)

  const [activeMarker, setActiveMarker] = useState<RoutePoint | null>(null)
  const hideTooltip = useCallback(() => setActiveMarker(null), [])
  useHideOnMapInteraction(hideTooltip)

  const fadeDistances = useMemo(() => {
    const calculator = createDistanceCalculator(clusterMethod)
    return route.map((point) => calculator(clusterDistance, point))
  }, [route, clusterMethod, clusterDistance])

  return { activeMarker, setActiveMarker, fadeDistances, zoom, minFadeDistance, fadeDistance, cluster }
}

export const useReverseGeocodedAddress = (lat: number, lng: number, enabled: boolean) => {
  const [address, setAddress] = useState<string | null>(null)
  const reverseGeocodeMode = useFlag('reverseGeocodeMode', 'Google' as string)

  useEffect(() => {
    if (!enabled) return
    reverseGeocode(lat, lng, reverseGeocodeMode)
      .then((result) => setAddress(result ?? ''))
      .catch(captureError(() => setAddress('')))
  }, [lat, lng, reverseGeocodeMode, enabled])

  return address
}

export const useGeoFenceTitle = (geofenceId: string, enabled: boolean) => {
  const { data: geoFence } = useSingleGeoFence(
    enabled ? { id: Number.parseInt(geofenceId, 10) } : null,
  )
  return geoFence?.title ?? null
}
